import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { prop } from 'remeda';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { AccountLoader } from 'Components/AccountLoader';
import { BasicModal } from 'Components/BasicModal';
import { Typography } from 'Components/Typography';

import { supplementsFactsImages, titles } from 'assets/content/cart/supplements';

import { fetchSupplementsVariant } from 'dux/user/actions';
import { getSupplementsVariantSlug, getUserFirstName } from 'dux/user/selectors';

const StyledBasicModal = styled(BasicModal)`
  padding-left: 0;
  padding-right: 0;
  text-align: center;
`;

const Title = styled(Typography)`
  margin-bottom: 18px;
`;

const BottleTitle = styled(Typography)`
  font-weight: 500;
  color: ${theme.colors.neutral[800]};
`;

const Image = styled.img`
  width: 100%;
  height: auto;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 75%;
  }
`;

const LoaderWrapper = styled.div`
  height: 70%;
  margin-bottom: 50px;
`;

const LoaderText = styled(Typography)`
  margin-top: 20px;
`;

type SupplementsFactsModalProps = {
  closeModal: () => void;
  isModalOpen: boolean;
  firstname?: string;
  variantSlug?: string;
};

export const SupplementsFactsModal = ({
  firstname: initialFirstname,
  variantSlug: initialVariantSlug,
  closeModal,
  isModalOpen,
}: SupplementsFactsModalProps) => {
  const dispatch = useAppDispatch();
  const reduxFirstname: string | undefined = useAppSelector(getUserFirstName);
  const reduxVariantSlug: string | undefined = useAppSelector(getSupplementsVariantSlug);

  const firstname = initialFirstname ?? reduxFirstname;
  const variantSlug = initialVariantSlug ?? reduxVariantSlug;

  useEffect(() => {
    if (!variantSlug && isModalOpen) {
      dispatch(fetchSupplementsVariant());
    }
  }, [isModalOpen]);

  return (
    <StyledBasicModal isOpen={isModalOpen} onClose={closeModal}>
      <Title id={isModalOpen ? 'modal-title' : ''} markupName="p" variant="p2">
        {firstname ? `${firstname}’s ` : 'Your '}Root Source<sup>®</sup> Hair Supplements
      </Title>
      {!variantSlug ? (
        <LoaderWrapper>
          <AccountLoader />
          <LoaderText>We are computing your supplements formula</LoaderText>
        </LoaderWrapper>
      ) : (
        <div id={isModalOpen ? 'modal-description' : ''}>
          {/* Bottle 1 */}
          <BottleTitle markupName="p" variant="mono3">
            {titles.core}
          </BottleTitle>
          <Image
            alt="Supplements core facts - First jar"
            data-testid="supplements-core-facts-img"
            src={prop(supplementsFactsImages, `${variantSlug}_facts`)?.src}
          />

          {/* Bottle 2 */}
          <BottleTitle markupName="p" variant="mono3">
            {titles.softGel}
          </BottleTitle>
          <Image
            alt="Supplements softgel facts - Second jar"
            data-testid="supplements-softgel-facts-img"
            src={supplementsFactsImages.supplementSoftgelFacts.src}
          />
        </div>
      )}
    </StyledBasicModal>
  );
};
